import React, { useContext, useState } from "react";
import { Container } from "react-bootstrap";
import { useDispatch } from "react-redux";
import FilterSection from "../../../organisms/Sections/filter-controller-section/FilterSection";
import FullWidthCard, {
  CardContentItem,
} from "../../../organisms/Cards/full-width-card/FullWidthCard";
import HelperLoader from "../../../molecules/HelperLoader/HelperLoader";
import Pagination from "../../../molecules/Pagination/Pagination";
import Button from "../../../atoms/Buttons/Button/Button";
import BreadCrumb from "../../../organisms/Breadcrumb/BreadCrumb";
import Headline from "../../../organisms/Headline/Headline";
import UseAuthConfig from "../../../../hooks/useAuthConfig";
import filterContentLeads from "../../../../utils/helpers/helperDataset/lead-management-contents/filterContentLeads.json";
import cardContent from "../../../../utils/helpers/helperDataset/lead-management-contents/cardContentLeads.json";
import useAllLeads from "../../../../hooks/useAllLeads";
import useDownloadLeads from "../../../../hooks/useDownloadLeads";
import { UserRole } from "../../../../utils";
import useUserRole from "../../../../hooks/useUserRole";
import { LEAD_IDENTIFIERS } from "../../../../pages/leads-management/models/ILead";
import LeadStatusDefinitions from "../../../organisms/LeadStatusDefinitions/LeadStatusDefinitions";
import LoaderHome from "../../../atoms/Loader-home/Loader-home";
import { getLeadRatingText } from "../../../../utils/getLeadRatingText";
import { UploadFileParams } from "../../../../slices/users/types";
import { uploadToQueue } from "../../../../slices/users/userManagementSlice";
import { AppDispatch } from "../../../../slices/store";
import Toaster from "../../../atoms/Toaster/toast";
import { TodosContext } from "../../../../context";
import { useNavigate } from "react-router-dom";

const AllLeadsTemplate: React.FC = () => {
  const dispatch = useDispatch<AppDispatch>();
  const { protectedResources } = UseAuthConfig();
  const { addAlert } = useContext(TodosContext);
  const navigate = useNavigate();
  // add these states
  const [retryToast, setRetryToast] = useState({
    message: "",
    severity: "success" as "success" | "failed",
    trigger: 0,
  });

  const {
    leadDetails,
    status,
    isLoading,
    activePage,
    handleSetFilteredValue,
    handlePageChange,
    handleDeleteCard,
    pageSize,
  } = useAllLeads();

  const { handleConfirm, isLoadingDownloads, handleSelectedConfirm } =
    useDownloadLeads(protectedResources);

  const userRole = useUserRole();
  const [selectedLeadIds, setSelectedLeadIds] = React.useState<number[]>([]);

  const leads = leadDetails?.data ?? [];
  const selectedCount = selectedLeadIds.length;

  const toggleLeadSelection = (leadId: number) => {
    setSelectedLeadIds((prev) =>
      prev.includes(leadId)
        ? prev.filter((id) => id !== leadId)
        : [...prev, leadId]
    );
  };

  const isLeadSelected = (leadId: number) => selectedLeadIds.includes(leadId);

  const handleRetryLead = async (leadId: number) => {
    const params: UploadFileParams = {
      accessToken: localStorage.getItem("AccessToken") || "",
      url: protectedResources.apiTodoList.endpoint + "/resources/upload-to-queue/",
      ReferenceId: leadId,
      Reference: 2,
      selectedFile: null,
    };

    try {
      const result = await dispatch(uploadToQueue(params)).unwrap();
      if (result?.status === "success") {
        addAlert("Lead retry initiated successfully.", "success", true);
        navigate(`/leads/all-leads/${leadId}`);
      } else {
        addAlert("Failed to initiate lead retry. Please try again.", "failed", true);
      }
    } catch (error) {
      addAlert("Lead retry failed. Please try again.", "failed", true); 
      navigate("/");
    }
  };

  return (
    <Container fluid className="layout-bar">

      <BreadCrumb />

      <Headline
        name="All Leads"
        subHeading="Smarter lead management for better conversions"
      />

      <div className="content-wrapper">
        <div className="filter-wrapper">
          <FilterSection
            {...filterContentLeads}
            handleInputText={handleSetFilteredValue}
          />

          <div className="download-btn-section d-flex align-items-center gap-3">
            {selectedCount > 0 && (
              <div className="clear-btn">
                <Button
                  cssClass="btn-secondary"
                  text="Clear Selection"
                  onClick={() => setSelectedLeadIds([])}
                />
              </div>
            )}

            {userRole !== UserRole.Operator && leads.length > 0 && (
              <Button
                cssClass="btn-primary"
                text={
                  selectedCount > 0 ? `Export (${selectedCount})` : "Export All"
                }
                icon="download"
                onClick={() => {
                  if (selectedLeadIds.length > 0) {
                    handleSelectedConfirm(selectedLeadIds);
                  } else {
                    handleConfirm(LEAD_IDENTIFIERS.ALL_LEADS);
                  }
                }}
                activeColor="#ffffff"
              />
            )}
          </div>
        </div>
      </div>
      <div className="content-wrapper">
        {leads.length > 0 ? (
          leads.map((lead: any, key: number) => {
            const updatedLead = {
              ...lead,
              rating: getLeadRatingText(lead.rating),
            };

            return (
              <FullWidthCard
                key={lead.id}
                cardContent={cardContent as CardContentItem[]}
                data={updatedLead}
                deleteCard={handleDeleteCard}
                isSelected={isLeadSelected(lead.id)}
                onSelect={() => toggleLeadSelection(lead.id)}
                isLeads={true}
                retryLead={(lead) => {
                  handleRetryLead(lead.id);
                }}
                canEdit={false}
              />
            );
          })
        ) : (
          <HelperLoader
            status={status}
            totalRecords={leadDetails?.totalRecords}
          />
        )}

        <Toaster alert={retryToast} />

        <div className="pagination-section">
          <Pagination
            pageCount={leadDetails?.totalPages ?? 0}
            pageNumber={activePage}
            onPageChange={(page: number) =>
              handlePageChange({ selected: page - 1 })
            }
            totalPages={leadDetails?.totalPages ?? 0}
            pageSize={pageSize}
            totalRecords={leadDetails?.totalRecords ?? 0}
            label="leads"
          />
        </div>
      </div>

      <LeadStatusDefinitions />

      {(isLoading || isLoadingDownloads) && (
        <>
          <LoaderHome />
          <LoaderHome />
          <LoaderHome />
        </>
      )}
    </Container>
  );
};

export default AllLeadsTemplate;

